/**
 * 聚合应用组件（react-dnd实现）
 *  1.拖拽实现灵活
 *  2.支持跨组拖拽
 *  3.支持从聚合应用往外拖拽
 *
 * 需要注意的点：
 *  1.拖拽聚合应用内部的应用不能移动整个聚合应用
 *  2.需要区分聚合应用内外边界（边界内没有placeholder）
 *  3.从聚合应用外如何拖拽到聚合应用内
 *
 * created by liyaoh 2021-06-16
 */
import React, { Fragment } from "react";
import { DropTarget } from "react-dnd";
import { connect } from "react-redux";
import { SizeMe } from "react-sizeme";
import {
    setAppGroupData,
    setDroppedAppGroupItem,
    updateGroupList,
    setGroupDragable,
    setDefaultAppGroupData,
    initGroups,
} from "Store/GridLayout/action";
import Notice from "Components/Notice";
import DragModal from "./DragModal";
import AppItem from "./AppItem";
import { langCheck, deleteRecentVisit, queryRecentVisit } from "Pub/js/utils";
import {
    setGridDroppable,
    DragTypes,
    getRepeatItems,
    getUniqueItems,
    APP_DEFAULT_SIZE,
    checkApp,
    findDataByCompactApp,
    genNameNum,
    createEmptyExternalLink,
    getScale,
    getAppTabArr,
    APPGROUPCONSTANT,
} from "../utilService";
import AppGroupAction from "./action";
import { cloneDeep } from "lodash";
import classnames from "classnames";
import platform from "Pub/js/platform";
const { PubSub } = platform;
import QuickLaunchModal from "./QuickLaunchModal";
import Ajax from "Pub/js/ajax";
import { getCurrentLangIndex, getMultiLangValue } from "Pub/js/getMulti";
import EmptyImg from "Assets/images/empty.svg";
import AppGroupSide from './AppGroupSide';
import { ControlTip } from "Components/ControlTip";

const containerPadding = 16;
let defaultWidth = 98;
const appGroupTarget = {
    canDrop(props, monitor) {
        if (checkApp(props.item).isQuickLaunch) { // 快速发起不允许放置
            return false;
        }

        const dragItem = monitor.getItem();
        return props.id !== dragItem.appGroupId;
    },
    drop(props, monitor, component) {
        let moved = true;
        const dragItem = monitor.getItem();
        const isOnlyThisOne = monitor.isOver({ shallow: true });
        if (monitor.didDrop()) {
            return;
        }
        if (isOnlyThisOne) {
            //从左侧拖拽设置拖拽成功回调
            component.dragFromLeftComplete(dragItem);
            const newItems = component.addApps({ from: dragItem.type, data: dragItem.cardList });
            moved = newItems.length > 0;
            PubSub.publish("onRemoveGridLayoutPlaceholder"); //清除GridLayout的placeholder
        }
        return { type: DragTypes.APP_GROUP, moved, appGroupId: props.id };
    },
};
@DropTarget([DragTypes.APP_GROUP_ITEM, DragTypes.SIDE_CARD], appGroupTarget, function (
    connect,
    monitor,
) {
    return {
        connectDropTarget: connect.dropTarget(),
        isOver: monitor.isOver(),
        isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
    };
})
class AppGroup extends React.Component {
    constructor(props) {
        super(props);
        this.appGroupAction = new AppGroupAction({
            appGroupMap: props.appGroupMap,
            id: props.id,
            updateAppGroup: this.upAppGroup,
        });
        this.state = {
            isShowAddModal: false,
            recentVisitData: [],
            activeTab: this.getActiveTab(props.item),
            widgetSide: null,
        };
    }

    componentDidMount() {
        const { item, renderHeaderCustomArea } = this.props;
        const checkItem = checkApp(item);
        if (checkItem.isQuickLaunch) { // 快速发起特有的背景
            renderHeaderCustomArea(<div className="quick-launch-bg-img"></div>, 'quick-launch-header-custom');
        }

        this.getGroupData();
        //监听设置GridLayout Droppable事件
        this.dropPubsub = PubSub.subscribe("onGridLayoutDropEnd", (action, res) => {
            this.deleteAppGroupItem(res.draggingItems);
        });
        //监听设置更新聚合应用缓存事件（主要是移动弹框移动聚合需要更新缓存）
        this.cachePubsub = PubSub.subscribe("updateAppGroupCache", (action, res) => {
            this.appGroupAction.updateCurrentAppGroup(this.props.appGroupMap[this.props.id]);
        });

        // 监听保存，处理快发起部件内容的保存
        this.savePubsub = PubSub.subscribe("SetAppComplete", (action, data) => {
            if (checkApp(this.props.item).isQuickLaunch) {
                this.saveQuickLaunch(data);
            }
        });

        // 监听快速 常用应用 发起数据变化
        this.quickLaunchPubsub = PubSub.subscribe("UpdateAppGroupData", (action, { id, data }) => {
            const { id: appId } = this.props;
            if (id === appId) {
                this.appGroupAction.updateCurrentAppGroup(data, false);
            }
        });
        // 常用应用 最近访问请求
        this.getRecentData(true);

        this.editablePubsub = PubSub.subscribe("onChangeDesktopEditable", isEdit => {
            //常用应用 浏览态切到编辑态时 显示自定义按钮区域
            setTimeout(() => { isEdit && this.renderCustomArea(); }, 50);
        });
        // 拖拽新的或生成新的常用应用时
        if (this.props.editable) {
            this.renderCustomArea();
        }

        // 监听导航栏最近访问
        this.pubsubRecentVisit = PubSub.subscribe(APPGROUPCONSTANT.RECENTVISIT, (action, data) => {
            this.setState({ recentVisitData: data });
        });
    }

    shouldComponentUpdate() {
        if (this.contentRef && this.contentRef.getBoundingClientRect().width === 0) {
            // 首页处于失活状态，阻止更新，不然会导致计算错误
            return false;
        }
        return true;
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isOver && this.props.isOver) {
            // You can use this as enter handler
            setGridDroppable(false); //禁用GridLayout的droppable
            PubSub.publish("onRemoveGridLayoutPlaceholder"); //清除GridLayout的placeholder
        }

        if (prevProps.isOver && !this.props.isOver) {
            // You can use this as leave handler
            setGridDroppable(true); //拖拽出边界恢复GridLayout的droppable
        }

        if (prevProps.isOverCurrent && !this.props.isOverCurrent) {
            // You can be more specific and track enter/leave
            // shallowly, not including nested targets
        }
    }

    componentWillReceiveProps(nextProps) {
        const { appGroupMap, droppedAppGroupItem, id, item } = nextProps;
        const { sideData = {} } = this.props && this.props.item;
        //桌面应用拖拽到聚合应用中
        if (droppedAppGroupItem.id && droppedAppGroupItem.id === id) {
            this.addApps(
                { from: "group", data: droppedAppGroupItem.data },
                undefined,
                droppedAppGroupItem.callback,
            );
            this.props.setDroppedAppGroupItem(null); //清除放置item
        }
        //更新AppGroupAction里缓存的数据
        if (nextProps.appGroupMap.updateAll) {
            this.appGroupAction.updateCurrentAppGroup(appGroupMap[id], false);
            this.props.setAppGroupData({ ...appGroupMap, ...{ updateAll: false } }); //用完之后清掉防止死循环
        }
        // 常用应用侧拉弹框数据改变 需要重新设置选中 tab
        const { appTab = getAppTabArr(sideData) } = sideData;
        const { appTab: nextAppTab } = item && item.sideData;
        if (!_.isEqual(appTab, nextAppTab) && appTab && nextAppTab) {
            const activeTab = this.getActiveTab(item);
            this.getRecentData(activeTab === APPGROUPCONSTANT.RECENTVISIT).then((res => {
                if (res) {
                    this.setState({ activeTab }, () => {
                        //编辑态自定义按钮区域
                        this.renderCustomArea();
                    });
                }
            }));
        }
    }
    componentWillUnmount() {
        //移除订阅
        PubSub.unsubscribe(this.dropPubsub);
        PubSub.unsubscribe(this.cachePubsub);
        PubSub.unsubscribe(this.savePubsub);
        PubSub.unsubscribe(this.quickLaunchPubsub);
        PubSub.unsubscribe(this.editablePubsub);
        PubSub.unsubscribe(this.pubsubRecentVisit); //移除订阅

    }

    // 更新聚合应用数据
    upAppGroup = (key, groupData, upSide) => {
        if (upSide) {
            // 更新一下侧拉数据
            const { item, onSideChange } = this.props;
            const { sideData } = item;
            sideData.cardList = groupData;
            onSideChange(sideData);
        }

        this.props.setAppGroupData(key, groupData);
    }

    // 常用应用获取当前选择 tab
    getActiveTab = item => {
        // const { item } = this.props;
        let appTab = getAppTabArr(item && item.sideData);
        if (appTab) {
            const showTab = appTab.filter(item => item.select);
            const activeTab = showTab && showTab[0] && showTab[0].key || APPGROUPCONSTANT.APP;
            return activeTab;
        }
    }
    // 获取数据
    getGroupData() {
        const { item, groupId, userId, relateidObj, workbenchid } = this.props;
        const checkItem = checkApp(item);
        const { sideData = {} } = item;
        if (item.isNew && !checkItem.isPublished) { // 新拖出来的，这里处理一下默认名字问题（发布出的不用处理，已经有了）
            const langIndex = getCurrentLangIndex();
            if (!sideData.name) {
                sideData.name = { [`name${langIndex}`]: { value: item.name } };
            }
            this.updateGroupAppPropertyData('sideData', sideData);
            return;
        }
        if (!checkItem.isQuickLaunch || this.appGroupAction.getData().length) {return;} // 已经有数据的话不再请求接口（在编辑态懒加载时，数据有更改的情况下滚动出可视范围再回来，会重新挂载，此时接口数据并未更新），初次加载即可
        // 下边都是针对快速发起的

        // 参数: workbenchid工作台主键，pk_quicklaunch主键(不传则查出改快速发起所有部件信息)
        const requestParams = {
            workbenchid,
            pk_group_app: item.cardid, // 快速发起部件主键
            // pk_group: groupId, // 所属集团
            isuser: relateidObj.code,
            isreleasepart: item.isreleasepart,
        };
        if (relateidObj.code == 1) {
            requestParams.relationid = userId; // 用户id，用户桌面配置传，工作台管理过来的不传
        }
        if (relateidObj.type == 'workbench') { // 工作台过来的
            requestParams.workbenchid = relateidObj.data;
        }
        Ajax({
            data: requestParams,
            url: `/nccloud/platform/appregister/queryquicklaunch.do`,
            loading: true,
            success: ({ data }) => {
                if (data.success) {
                    const dataList = data.data;
                    // 排序
                    dataList.sort((a, b) => { return (Number(a.position) - Number(b.position)); });

                    this.appGroupAction.updateCurrentAppGroup(dataList);

                    this.setInitData();
                }
            },
        });
    }

    // 快速发起是单独的接口，获取完设置一下初始化的数据
    setInitData() {
        this.props.setDefaultAppGroupData(_.cloneDeep(this.props.appGroupMap));
        this.props.initGroups(this.props.groups);
    }

    // 保存快速发起
    saveQuickLaunch(data) {
        const { appGroupMap, id, groupId, userId, item, relateidObj } = this.props;
        const { isuser, workbenchid } = data;
        const commonParams = {
            workbenchid,
            pk_group_app: item.cardid, // 快速发起部件主键
            // pk_group: groupId, // 所属集团
            isreleasepart: item.isreleasepart,
        };
        if (isuser == 1) {
            commonParams.relationid = userId; // 用户id，用户桌面配置传，工作台管理过来的不传
        }
        if (relateidObj.type == 'workbench') { // 工作台过来的
            commonParams.workbenchid = relateidObj.data;
        }
        const requestData = {
            ...commonParams,
            isuser,
            data: appGroupMap[id].map((item, index) => {
                const result = {
                    ...item,
                    position: index,
                    ...commonParams,
                };
                if (result.hasOwnProperty('appperm')) {
                    result['appperm'] = '0'; // 重置应用权限
                }
                return result;
            }),
        };
        Ajax({
            data: requestData,
            url: `/nccloud/platform/appregister/savequicklaunch.do`,
            loading: true,
            success: () => {
                console.log('请求成功');
                // 这里更新数据是因为可能会存在某些原因导致保存接口执行较慢，此时先执行了查询接口导致数据展示错误
                this.appGroupAction.updateCurrentAppGroup(appGroupMap[id]);
            },
        });
    }

    //拖拽到content删除聚合应用item
    deleteAppGroupItem = draggingItems => {
        const { id, draggingAppGroupItem } = this.props;
        const { data, from } = draggingItems;
        //删除聚合应用拖拽的item
        if (
            from === DragTypes.APP_GROUP_ITEM &&
            id === draggingAppGroupItem.appGroupId &&
            data.length
        ) {
            this.appGroupAction.remove(draggingAppGroupItem.index);
        }
    };

    //校验聚合应用内是否有重复应用并返回去重后数据
    validateRepeatItems = dragList => {
        const appDragList = dragList.filter(a => checkApp(a).isApp || checkApp(a).isExternalLink); //只有应用和外部链接能拖到聚合应用中
        const { json, appGroupMap, id, item } = this.props;
        const appGroupData = appGroupMap[id];
        const repeatCards = getRepeatItems(appGroupData, appDragList, "appcode");
        const newRepeatCards = repeatCards.filter(
            item => !item.isNotRepeat && !checkApp(item).isExternalLink,
        ); //去掉不校验重复的cards
        //已添加card不再重复添加
        if (newRepeatCards.length) {
            const uniqueCards = getUniqueItems(appGroupData, appDragList, "appcode"); //去重后cards
            const repeatCardsName = repeatCards.map(r => r.name);
            Notice({
                status: "warning",
                msg: `“${item.name}”${json["DesktopSetting-000057"]}${repeatCardsName.join(",")},${json["DesktopSetting-000058"]
                }`, //xx内已存在相同应用采购订单、XXX，此次不再重复添加
            });
            return uniqueCards;
        }
        return appDragList;
    };

    //拖拽添加到聚合应用内 targetIndex:拖拽到聚合应用内应用的index
    addApps = (dragItem, targetIndex, cb) => {
        let newItems = this.validateRepeatItems(dragItem.data);
        //添加到聚合应用尺寸变为应用默认尺寸
        newItems = newItems.map(app => {
            //从左侧拖外部链接到聚合应用内
            if (checkApp(app).isExternalLink && dragItem.from === DragTypes.SIDE_CARD) {
                app = createEmptyExternalLink(app, { name: this.genExlinkName() });
            }
            return { ...app, ...APP_DEFAULT_SIZE };
        });
        this.appGroupAction.add(newItems, targetIndex, true);
        //添加成功执行callback
        if (cb && newItems.length) {cb(this.props.id);}
        return newItems;
    };

    //弹框新增应用
    updateApps = dataList => {
        this.appGroupAction.updateCurrentAppGroup(dataList);
        if (!this.props.editable) {
            // 浏览态添加常用应用
            this.saveCommonUsedApp(dataList);
        }
    };

    saveCommonUsedApp(dataList) {
        if (this.props.isRespWorkbench) { // 需要生成用户级桌面，请求保存接口
            PubSub.publish("saveDesktopOutside");
            return;
        }
        const { item } = this.props;
        const requestParams = {
            pk_group_app: item.pk_group_app, // 常用应用主键
            data: dataList.map((d, index) => {
                return {
                    appid: d.appid, // 应用主键
                    gridy: index, // 坐标
                    groupid: item.pk_app_group, // 当前分组主键
                    menuitemid: d.cardid,
                    parttype: d.parttype,
                };
            }),
        };
        Ajax({
            data: requestParams,
            url: `/nccloud/platform/appregister/savecommonapp.do`,
            loading: true,
            success: ({ data }) => {
                if (data.success) {
                    Notice({
                        status: "success",
                        msg: langCheck('0000PUB-000020'), // 操作成功
                    });
                }
            },
        });
    }

    //浏览态添加聚合应用
    handleAddGroupApp = () => {
        this.toggleAddModalShow(true);
    };

    //切换修改聚合应用modal显示
    toggleAddModalShow = isShow => {
        let { isShowAddModal } = this.state;
        isShowAddModal = typeof isShow === "boolean" ? isShow : !isShowAddModal;
        this.setState({ isShowAddModal });
        if (this.currentHandleItem && !isShowAddModal) { // 关闭弹窗清除当前操作数据
            this.currentHandleItem = null;
        }
        setGridDroppable(!isShowAddModal);
    };
    //选中item
    handleCheckboxChange = (index, checked) => {
        this.appGroupAction.check(index, checked);
        //取消选中聚合应用内应用选中同时取消聚合应用选中状态
        if (!checked) {
            this.updateGroupAppPropertyData("isChecked", false); //取消聚合应用选中
        }
    };

    //删除item
    handleRemoveItem = (e, index) => {
        e.stopPropagation();
        this.appGroupAction.remove(index, true);
    };

    // 改名
    handleNameChange = value => {
        const { item } = this.props;
        const { sideData } = item;
        sideData.name = value;
        this.updateGroupAppPropertyData('sideData', sideData);
    };

    // 名字浏览or编辑态变化
    handleEditStatusChange = isEdit => {
        if (isEdit) {
            this.props.setGroupDragable(false); // 编辑态禁用分组拖拽
        } else {
            this.props.setGroupDragable(true); // 浏览态态恢复分组拖拽
        }
    }

    //修改分组内应用某个字段的值
    updateGroupAppPropertyData = (property, value) => {
        let groups = cloneDeep(this.props.groups);
        const { group, groupIndex, appIndex } = findDataByCompactApp(groups, this.props.id);
        if (group.apps[appIndex]) {
            if (typeof property === "string") {
                group.apps[appIndex][property] = value;
            } else {
                group.apps[appIndex] = { ...group.apps[appIndex], ...property };
            }
        }
        groups[groupIndex] = group;
        this.props.updateGroupList(groups);
    };

    //生成外部链接名称
    genExlinkName = () => {
        const { appGroupMap, id, json } = this.props;
        const nameNation = json["DesktopSetting-000064"]; //常用链接
        const nameCount = genNameNum({
            nameNation,
            data: appGroupMap[id],
            condition: (a, num) => a.name === `${nameNation}${num}`,
        });
        return `${nameNation}${nameCount}`;
    };

    //从左侧拖拽成功设置标示
    dragFromLeftComplete = dragItem => {
        if (dragItem.type === DragTypes.SIDE_CARD) {
            this.setState({ dropCompleteFlag: true });
        }
        this.props.setGroupDragable(true); //恢复分组拖拽
    };

    clearDragCompleteFlag = () => {
        this.setState({ dropCompleteFlag: false });
    };
    //外部链接从左侧直接拖到聚合应用内
    handleAddWidgetComplete = (appItem, data = {}) => {
        if (!data) {return;}
        this.setState({ dropCompleteFlag: false });
        //新增外部链接把返回id赋值给cardid
        if (checkApp(appItem).isExternalLink && data) {
            this.appGroupAction.updateAppProperties(appItem.cardid, {
                cardid: data.pk_group_app,
                pk_group_app: data.pk_group_app,
            });
        }
    };
    //获取 常用应用 左右排列时宽度
    getAroundWidth = () => {
        const { item } = this.props;
        if (checkApp(item).isCommonUsedApp) {
            if (item.sideData && item.sideData.cardArrangement === 'around') {
                defaultWidth = 130;
            } else {
                defaultWidth = 98;
            }
        } else {
            defaultWidth = 98;
        }
    }
    // 聚合内应用宽度
    getAppItemWidth(size) {
        const { item, appGroupMap, id } = this.props;
        // orderType = getAppGroupOrderType(groupItem.sideData && groupItem.sideData.cardArrangement && groupItem.sideData.cardArrangement);

        const appGroupData = appGroupMap[id] || [];
        // let data = cloneDeep(appGroupData) || [];
        if (checkApp(item).isQuickLaunch) {
            if (item.sideData && item.sideData.cardArrangement && item.sideData.cardArrangement.type === 'setCol') {
                const count = item.sideData.cardArrangement.value.setCol;
                return `calc(${1 / count * 100}% - 8px)`; // 减去边距
            }
            if (size && size.width) {
                const width = size.width;
                const num = Math.floor(width / 134);
                if (num > appGroupData.length) {
                    return `calc(${1 / appGroupData.length * 100}% - 8px)`; // 减去边距
                }
                return `calc(${1 / num * 100}% - 8px)`; // 减去边距
            }
        }
        return defaultWidth;
    }
    // 快速发起内部应用 自适应定宽 高度自适应
    getAppItemHeight = size => {
        const { appGroupMap, id } = this.props;
        const appGroupData = appGroupMap[id] || [];
        if (size) {
            const { width } = size;
            const num = Math.floor(width / 134);
            const num1 = Math.ceil(appGroupData.length / num);
            return `calc(${1 / num1 * 100}% - 8px)`; // 减去边距
        }
    }
    //根据容器宽度计算占位元素
    genAppGroupData = (size, keepSame = false) => {
        const { appGroupMap, id, siderCollapsed } = this.props;
        const appGroupData = appGroupMap[id];
        let data = cloneDeep(appGroupData) || [];
        if (keepSame) { // 保持不变，不需要占位元素
            return data;
        }
        const { recentVisitData, activeTab } = this.state;
        if (activeTab === APPGROUPCONSTANT.RECENTVISIT) {
            // 最近访问
            data = recentVisitData;
        }
        let appWidth = this.getAppItemWidth(size);
        const $appItem = this.contentRef && this.contentRef.querySelector(".app-group-item");
        //展开左右侧拉时 宽度取item真实宽度
        if ($appItem) {
            appWidth = $appItem.getBoundingClientRect().width || appWidth;
        }

        if (size && size.width) {
            // 优先取getBoundingClientRect真实宽度，是不包含滚动条的
            const innerWidth = (this.contentRef ? this.contentRef.getBoundingClientRect().width : size.width) - containerPadding * 2 * getScale().narrow; // width-padding 需要乘以缩放比例
            const colNum = Math.max(Math.floor(innerWidth / appWidth), 1); //一行显示多少元素
            const rowNum = Math.ceil(data.length / colNum); //显示多少行
            const fullNum = colNum * rowNum; //占满显示多少个
            // console.log(size.width)
            // console.log('innerWidth', innerWidth)
            // console.log('appWidth', appWidth)
            // console.log('data', data.length)
            // console.log('colNum', colNum)
            //没占满补空位
            if (fullNum !== data.length) {
                let emptyArr = new Array(fullNum - data.length).fill(null);
                data = data.concat(emptyArr);
            }
        }
        return data;
    };
    //渲染编辑态自定义按钮
    renderCustomArea = () => {
        this.props.renderEditCustomArea && this.props.renderEditCustomArea(this.renderBtn());
    };

    getNameObj = () => {
        const { item } = this.props;
        const nameResult = getMultiLangValue('name', item.sideData);
        return nameResult;
    }
    // 最近访问接口
    getRecentData = flag => {
        const { item } = this.props;
        const checkItem = checkApp(item);
        if (!checkItem.isCommonUsedApp) {return;} // 是常用应用才发请求
        return new Promise(resolve => {
            if (flag) {
                queryRecentVisit().then(result => {
                    this.setState({ recentVisitData: result });
                    resolve(true);
                });
            } else {
                resolve(true);
            }
        });
    };
    // 清空最近访问
    clearRecentVisit = () => {
        ControlTip({
            status: "warning",
            title: langCheck("0000PUB-000311"),
            msg: langCheck("0000PUB-000281") /* 国际化处理： 确定要清空最近访问?*/,
            zIndex: 1000,
            onOk: () => {
                deleteRecentVisit().then(flag => {
                    if (flag) {
                        Notice({
                            status: "success",
                            msg: langCheck('0000PUB-000406'), //删除成功-多语
                        });
                        this.setState({ recentVisitData: [] }, () => {
                            this.renderCustomArea(); // 渲染删除图标 

                        });
                    }
                });
            },
        });
    }
    // tab 切换
    setActiveTab = key => {
        const { item, onSideChange } = this.props;
        const appTab = getAppTabArr(item && item.sideData);
        this.getRecentData(key === APPGROUPCONSTANT.RECENTVISIT).then(res => {
            // 写回调里面 是为了先拿到最近访问数据 再设置选中tab 删除图标才能显示出来
            if (res) {
                appTab.forEach(c => {
                    if (c.key === key) {
                        c.select = true;
                    } else {
                        c.select = false;
                    }
                });
                onSideChange({ ...item.sideData, appTab });
                this.setState({ activeTab: key }, () => {
                    this.renderCustomArea(); // 渲染删除图标 

                });
            }
        });
    }
    renderAppTab = () => {
        const { activeTab } = this.state;
        const { item } = this.props;
        const appTab = getAppTabArr(item && item.sideData);
        const showAppTab = appTab.filter(item => item.show);
        return <Fragment>
            {
                appTab && appTab.map(current => {
                    const { title, key, show } = current;
                    return show && <span
                        key={key}
                        className={`name name-tab 
                            ${(activeTab === key && showAppTab.length === 2) ? 'name-select1' : ''}
                            ${(activeTab === key && showAppTab.length === 1) ? 'name-select2' : ''}`}
                        onMouseDown={e => { e.stopPropagation(); }}
                        onClick={() => { this.setActiveTab(key); }}
                        title={title}
                    >
                        {title}
                    </span>;
                })
            }
        </Fragment >;
    }
    renderTitle = () => {
        const { editable, item, renderLinkageIcons } = this.props;
        const checkObj = checkApp(item);
        return (
            <div className={`app-group-header ${checkObj.isQuickLaunch ? 'header-quick-launch' : ''}`}>
                {
                    checkApp(item).isCommonUsedApp && <div className="group-header-left">
                        {this.renderAppTab()}
                        {renderLinkageIcons()}
                    </div>
                }
                {!editable && this.renderBtn()}
            </div>
        );
    };

    renderApps = size => {
        const { activeTab } = this.state;
        const { id, item, relateidObj } = this.props;
        const checkItem = checkApp(item);
        this.getAroundWidth();
        const style = { width: `${defaultWidth}px`, margin: '5px 0 8px 0' };
        const isSetCol = item.sideData && item.sideData.cardArrangement && item.sideData.cardArrangement.type === 'setCol';
        if (checkItem.isQuickLaunch && isSetCol) {
            style.margin = '4px 8px 4px 0';
            style.width = `${this.getAppItemWidth(size)}`;
        }
        if (checkItem.isQuickLaunch && !isSetCol) {
            style.height = `${this.getAppItemHeight(size)}`;
            style.minHeight = `48px`;
            style.margin = `4px 8px 4px 0`;
            style.width = `${this.getAppItemWidth(size)}`;
        }
        // console.log(style)
        const isQuickLaunchAndAdaptive = checkItem.isQuickLaunch && !isSetCol;
        let newAppGroupData = this.genAppGroupData(size, checkItem.isQuickLaunch) || [];
        const groupItemContentStyle = checkItem.isQuickLaunch && isSetCol
            ?
            { margin: '8px 8px 16px 16px' }
            : checkItem.isQuickLaunch && !isSetCol ?
                {
                    padding: `8px 8px 8px ${containerPadding}px`,
                    height: '100%',
                    overflow: 'auto',
                }
                :
                {
                    padding: `0 ${containerPadding}px`,
                    justifyContent: 'space-around',
                    height: (newAppGroupData && !newAppGroupData.length) ? '100%' : 'auto',
                };
        // console.log(newAppGroupData)
        return (
            <div
                ref={ref => (this.contentRef = ref)}
                className="app-group-item-content"
                style={groupItemContentStyle}
            >
                {(newAppGroupData && newAppGroupData.length) ? newAppGroupData.map((app, index) => {
                    if (app !== null) {
                        return (
                            <div
                                className={classnames("app-group-item nodrag", { unauthorized: app.haspower === false })}
                                title={app.label ? app.label : app.name === 'null' ? '' : app.name} //最近访问label 常用应用 name
                                style={{ ...style }}
                            >
                                <AppItem
                                    key={app.cardid}
                                    item={app}
                                    groupItem={item}
                                    isQuickLaunchAndAdaptive={isQuickLaunchAndAdaptive}
                                    appGroupId={id}
                                    index={index}
                                    isChecked={app.isChecked}
                                    editable={this.props.editable}
                                    openApp={this.props.openApp}
                                    appGroupAction={this.appGroupAction}
                                    addApps={this.addApps}
                                    validateRepeatItems={this.validateRepeatItems}
                                    isAddComplete={this.state.dropCompleteFlag}
                                    dropCompleteFlag={this.state.dropCompleteFlag}
                                    dragFromLeftComplete={this.dragFromLeftComplete}
                                    onCheck={checked =>
                                        this.handleCheckboxChange(index, checked)
                                    }
                                    onDelete={e => this.handleRemoveItem(e, index)}
                                    // clearDragCompleteFlag={this.clearDragCompleteFlag}
                                    onAddWidgetComplete={this.handleAddWidgetComplete.bind(
                                        this,
                                        app,
                                    )}
                                    onEdit={() => {
                                        this.handleEditItem(index);
                                    }}
                                    relateidObj={relateidObj}
                                    activeTab={activeTab} // 区分常用链接还是最近访问
                                />
                            </div>
                        );
                    } else {
                        return <div className="empty-app-group-item" style={style} />;
                    }
                }) : this.renderEmptyBox()}
            </div>
        );
    };

    handleEditItem(index) {
        // console.log('handleEditItem', item);
        const { appGroupMap, id } = this.props;
        this.currentHandleItem = appGroupMap[id][index]; // 记录当前操作的，编辑的时候需要带出来,保持引用关系，编辑的时候直接改
        this.toggleAddModalShow();
    }

    handleRefresh = () => {
        this.setState({ isRotate: true });
        setTimeout(() => {
            this.setState({ isRotate: false });
        }, 1000);
        this.getRecentData(true);
    }
    // 刷新按钮
    renderRefreshArea = () => {
        const { isRotate } = this.state;
        return <div className='refresh-container'>
            {!isRotate ? <i className='iconfont icon-shuaxin' onClick={this.handleRefresh} /> : <div className='ball-clip-rotate'><div></div></div>}
        </div>;
    }
    // 自定义渲染图标
    renderBtn = () => {
        const { item, editable } = this.props;
        const { appcode } = item;
        const { activeTab, recentVisitData } = this.state;
        if (checkApp(item).isQuickLaunch) {return null;} // 快速发起 最近访问 不需要
        let iconDom = null;
        if (activeTab === APPGROUPCONSTANT.APP) {
            iconDom = <span className="icon-wrap" onClick={this.handleAddGroupApp}>
                <i className={`iconfont ${editable ? 'icon-zengjiazhuomian' : 'icon-ncc-sq'}`} fieldid={`icon-add-edit-${appcode}`} />
            </span>;
        }
        if (activeTab === APPGROUPCONSTANT.RECENTVISIT && recentVisitData && recentVisitData.length) {
            iconDom = <span className="icon-wrap" onClick={this.clearRecentVisit}>
                <i className="iconfont icon-shushanchu" style={{ fontSize: '16px' }} fieldid={`icon-shushanchu-${appcode}`} />
            </span>;
        }
        return iconDom;
    };

    //聚合应用hover样式
    getHoverStyle = () => {
        const {
            isOver,
            draggingItems: { from, data },
            item,
        } = this.props;
        if (isOver) {
            //从左侧拖
            if (from === DragTypes.SIDE_CARD) {
                const isAllWidgets = data.every(item => checkApp(item).isWidget);
                const hasLink = data.some(item => checkApp(item).isExternalLink);
                //从左侧拖非外链部件加个背景色
                if (isAllWidgets && !hasLink) {
                    return { backgroundColor: "#f0f0f0" };
                }
            }

            if (checkApp(item).isQuickLaunch) { // 快速发起不能拖放，不需要样式
                return {};
            }

            return { border: "1px solid red" };
        }
    };
    // 获取拖拽弹框标题
    getDragModalTitle = () => {
        const { item = {} } = this.props;
        const { sideData } = item;
        return getAppTabArr(sideData).filter(i => i.key === APPGROUPCONSTANT.APP)[0].title;
    }
    renderEmptyBox = () => {
        const { activeTab } = this.state;
        const { editable, isPublished } = this.props;
        let dom = <div className="group-empty">
            <div className="empty-content">
                <img className="empty-img" src={EmptyImg} />
                <div className="empty-text">{this.props.json['DesktopSetting-000092' /* 请添加发起卡片 */]}</div>
                {editable && !isPublished && <span className="empty-add" onClick={this.handleAddGroupApp}>{this.props.json['DesktopSetting-000091' /* 立即添加 */]}</span>}
            </div>
        </div>;
        if (!checkApp(this.props.item).isQuickLaunch) {
            dom = <div className="group-empty">
                <div className="empty-content">
                    <img className="empty-img" src={EmptyImg} />
                    {
                        activeTab === APPGROUPCONSTANT.RECENTVISIT ? <div className="empty-text">{this.props.json["DesktopSetting-000095" /* 暂无数据 */]}</div> :
                            <div className="empty-text">{this.props.json["DesktopSetting-000094" /* 请添加应用 */]}</div>
                    }

                    {editable && activeTab !== APPGROUPCONSTANT.RECENTVISIT && <span className="empty-add" onClick={this.handleAddGroupApp}>{this.props.json['DesktopSetting-000091' /* 立即添加 */]}</span>}
                </div>
            </div>;
        }
        return dom;
    }

    renderContent = () => {
        const { item } = this.props;
        const checkItem = checkApp(item);
        const { activeTab } = this.state;
        return <React.Fragment>
            {checkItem.isCommonUsedApp && this.renderTitle()}
            <div className="app-group-item-content-wrapper"
                style={checkItem.isQuickLaunch ? { height: `100%` } : { height: `calc(100% - 37px)` }}
            >
                <SizeMe>
                    {({ size }) => (
                        <div
                            className="app-group-item-container"
                            key={activeTab}
                            style={{
                                height: "100%",
                                overflow: "auto",
                            }}
                        >
                            {
                                this.renderApps(size)
                            }
                        </div>
                    )}
                </SizeMe>
            </div>
        </React.Fragment>;
    }

    render() {
        // console.log("appgroppppp", this.props.appGroupMap);
        const { connectDropTarget, appGroupMap, id, relateidObj, item, editable } = this.props;
        const { activeTab } = this.state;
        const checkItem = checkApp(item);
        const hoverStyle = this.getHoverStyle();
        const quickLaunchStyle = { ...hoverStyle };
        const dragModalTitle = this.getDragModalTitle();
        return connectDropTarget(
            <div className="app-group-content" style={checkApp(item).isQuickLaunch ? quickLaunchStyle : hoverStyle}>
                {
                    checkItem.isQuickLaunch && (!appGroupMap[id] || !appGroupMap[id].length)
                        ?
                        this.renderEmptyBox()
                        :
                        this.renderContent()
                }
                {this.state.isShowAddModal && (
                    checkApp(this.props.item).isQuickLaunch
                        ?
                        <QuickLaunchModal
                            title={this.props.item.name}
                            defaultData={appGroupMap[id]}
                            onOk={this.updateApps}
                            onCancel={this.toggleAddModalShow}
                            currentData={this.currentHandleItem}
                            relateidObj={relateidObj}
                        />
                        :
                        <DragModal
                            json={this.props.json}
                            title={dragModalTitle}
                            defaultData={appGroupMap[id]}
                            onOk={this.updateApps}
                            onCancel={this.toggleAddModalShow}
                            editable={editable}
                        />
                )}
                {/* {
                    activeTab === APPGROUPCONSTANT.RECENTVISIT && this.renderRefreshArea()
                } */}
            </div>,
        );
    }
}
AppGroup.SidePanel = AppGroupSide;

export default connect(
    state => ({
        appGroupMap: state.gridLayoutData.appGroupMap,
        draggingAppGroupItem: state.gridLayoutData.draggingAppGroupItem,
        json: state.gridLayoutData.json,
        droppedAppGroupItem: state.gridLayoutData.droppedAppGroupItem,
        groups: state.gridLayoutData.groups,
        isUpdateAppGroupCache: state.gridLayoutData.isUpdateAppGroupCache,
        draggingItems: state.gridLayoutData.draggingItems,
        siderCollapsed: state.gridLayoutData.siderCollapsed,
        workbenchid: state.appData.workbenchId,
        groupId: state.appData.groupId,
        userId: state.appData.userId,
        isRespWorkbench: state.gridLayoutData.isRespWorkbench,
    }),
    {
        setAppGroupData,
        setDroppedAppGroupItem,
        updateGroupList,
        setGroupDragable,
        setDefaultAppGroupData,
        initGroups,
    },
)(AppGroup);
